import React, { useMemo, useCallback } from "react";
import {
  BarChart as RechartsBarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LabelList,
} from "recharts";

interface BarChartData {
  labels: string[];
  datasets: Array<{
    label: string;
    data: number[];
  }>;
}

interface BarChartProps {
  data: BarChartData;
  height?: number;
  showGrid?: boolean;
  showTooltip?: boolean;
}

const BarChart: React.FC<BarChartProps> = ({
  data,
  height = 280,
  showGrid = true,
  showTooltip = true,
}) => {
  const { labels, datasets } = data;
  const dataset = datasets[0]; // For MVP, we'll use the first dataset

  // Memoize chart data to prevent unnecessary re-renders
  const chartData = useMemo(
    () =>
      labels.map((label, index) => ({
        name: label,
        value: dataset.data[index],
      })),
    [labels, dataset.data]
  );

  // Memoize CustomTooltip to prevent re-creation on every render
  const CustomTooltip = useCallback(
    ({ active, payload, label }: any) => {
      if (active && payload && payload.length) {
        return (
          <div className="bg-white p-4 border border-gray-200 rounded-lg shadow-lg max-w-xs">
            <div className="space-y-2">
              {/* Truncated label */}
              <div
                className="text-sm font-medium text-gray-900 truncate"
                title={label} // show full text on hover
              >
                {label}
              </div>

              {/* Dataset value */}
              <div className="text-sm text-gray-600">
                {dataset.label}:{" "}
                <span className="font-semibold text-gray-900">
                  {payload[0].value}
                </span>
              </div>
            </div>
          </div>
        );
      }
      return null;
    },
    [dataset.label]
  );

  return (
    <div className="w-full">
      <div className="text-center mb-3">
        <h6 className="text-lg font-bold text-gray-900">{dataset.label}</h6>
      </div>

      <div className="pt-2">
        <ResponsiveContainer width="100%" height={height}>
          <RechartsBarChart
            data={chartData}
            margin={{ top: 20, right: 30, left: 20, bottom: 40 }}
          >
            {showGrid && (
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            )}
            <XAxis
              dataKey="name"
              tick={{ fontSize: 11, fill: "#6b7280" }}
              axisLine={{ stroke: "#e5e7eb" }}
              tickLine={{ stroke: "#e5e7eb" }}
              angle={-45}
              textAnchor="end"
              height={80}
              interval={0}
              tickFormatter={(value: string) =>
                value.length > 15 ? value.substring(0, 15) + "…" : value
              }
            />
            <YAxis
              tick={{ fontSize: 12, fill: "#6b7280" }}
              axisLine={{ stroke: "#e5e7eb" }}
              tickLine={{ stroke: "#e5e7eb" }}
            />
            {showTooltip && <Tooltip content={<CustomTooltip />} />}
            <Bar
              dataKey="value"
              fill="#3b82f6"
              radius={[4, 4, 0, 0]}
              className="hover:fill-blue-600 transition-colors duration-200"
            >
              <LabelList
                dataKey="value"
                position="top"
                fill="#374151"
                fontSize={12}
                fontWeight={500}
              />
            </Bar>
          </RechartsBarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default BarChart;
